import {
  Box,
  Divider,
  Flex,
  IconButton,
  Image,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { ActionButton } from '@components/Buttons'
import { useAuth } from '@hb/react'
import {
  AnyObject,
  BaseAuthentication,
  Encounter,
  EncounterType,
  EncounterTypeConfig,
  getDateTimeString,
} from '@hb/shared'
import historyIcon from '@icons/history.svg'
import { PrintDisabledIcon, PrintIcon } from '@icons/PrintIcon'
import { EncounterErrorsBanner } from './EncounterErrorsBanner'
import { EncounterFormSettingsPopover } from './EncounterFormSettingsPopover'
import { ExamTypeHeaderContent } from './EncounterTypeHeaderContent.tsx'

interface EncounterFormHeaderTitleRowProps<T extends EncounterType> {
  typeName: string
  examId: string | null
  examType: T
  examConfig: EncounterTypeConfig<T> | null
  practiceId: string | null
  isPracticeSuperAdmin: boolean
  onOpenMyTemplates: () => void
  onOpenPracticeTemplates: () => void
  cannotPrintMessage: string | null
  printing: boolean
  onPrint: () => void
  hasAuthHistory: boolean
  encounter: Encounter | null
  onViewHistoricalAuth: (auth: BaseAuthentication<AnyObject> | 'current') => void
}

// Row 1: Exam name + type + template links + print button + auth-history popover.
// Title bar — 10px/20px padding · 13px #111 title · 10px sage template links (spec 0.6.2).
export const EncounterFormHeaderTitleRow = <T extends EncounterType>({
  typeName,
  examId,
  examType,
  examConfig,
  practiceId,
  isPracticeSuperAdmin,
  onOpenMyTemplates,
  onOpenPracticeTemplates,
  cannotPrintMessage,
  printing,
  onPrint,
  hasAuthHistory,
  encounter,
  onViewHistoricalAuth,
}: EncounterFormHeaderTitleRowProps<T>) => {
  const { authUser } = useAuth()
  return (
    <Flex align="center" py={1} px={3} w="100%" gap={2}>
      <Flex flex={1} align="baseline" gap={1.5} minW={0} flexWrap="wrap">
        <Text
          whiteSpace="nowrap"
          fontSize="14px"
          color="#111111"
          fontFamily="Open Sans"
          fontWeight={500}>
          {examId ? '' : 'New '}
          {typeName}
        </Text>
        {/* §E: Quick access to personal dot phrase templates */}
        {authUser?.uid && (
          <Text
            fontSize="12px"
            color="#4a7c59"
            flexShrink={0}
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={onOpenMyTemplates}>
            My Templates
          </Text>
        )}
        {/* §E: Practice super-admin access to shared practice templates */}
        {isPracticeSuperAdmin && practiceId && (
          <Text
            fontSize="12px"
            color="#4a7c59"
            flexShrink={0}
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={onOpenPracticeTemplates}>
            Practice Templates
          </Text>
        )}
      </Flex>
      {/* §M.7: Counter banner — replaces single-error orange pill */}
      <EncounterErrorsBanner />
      <ExamTypeHeaderContent examType={examType} config={examConfig} />
      {examId ? (
        <Tooltip
          hasArrow
          placement="right"
          bg={cannotPrintMessage ? 'gray.500' : 'green.500'}
          label={cannotPrintMessage ?? `Print ${typeName}`}>
          <IconButton
            aria-label={`Print ${typeName}`}
            icon={
              cannotPrintMessage ? <PrintDisabledIcon w={5} h={5} /> : <PrintIcon w={5} h={5} />
            }
            opacity={0.5}
            color={cannotPrintMessage ? 'gray.500' : 'black'}
            variant="ghost"
            size="sm"
            borderRadius="full"
            isLoading={printing}
            onClick={cannotPrintMessage ? undefined : onPrint}
          />
        </Tooltip>
      ) : null}
      <EncounterFormSettingsPopover />
      {hasAuthHistory && encounter?.authentication ? (
        <Popover strategy="fixed" trigger="click" placement="bottom-end">
          <PopoverTrigger>
            <IconButton
              borderRadius="full"
              size="sm"
              variant="ghost"
              opacity={0.5}
              aria-label="View authentication history"
              icon={<Image _dark={{ filter: 'invert(1)' }} src={historyIcon} />}
            />
          </PopoverTrigger>
          <Portal>
            <PopoverContent w="360px">
              <PopoverHeader
                borderTopRadius={4}
                bg="gray.50"
                _dark={{ bg: 'gray.900' }}
                py={1}
                fontWeight={500}
                fontFamily="hero-new">
                Authentication History
              </PopoverHeader>
              <PopoverBody p={0}>
                <VStack w="100%" divider={<Divider />} spacing={0}>
                  <Flex align="center" w="100%" gap={2} px={3} py={2}>
                    <Box flex={1} minW={0}>
                      <Text fontWeight={500}>
                        {encounter.authentication.providerFirst}{' '}
                        {encounter.authentication.providerLast}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {/* §1.6: authentication timestamps render to the second. */}
                        {getDateTimeString(
                          encounter.authentication.authenticatedOn,
                          'short',
                          false,
                          true,
                        )}
                      </Text>
                      {encounter.draftData ? (
                        <Text fontSize="xs" color="orange.500" fontWeight={500}>
                          Unsaved edits pending
                        </Text>
                      ) : null}
                    </Box>
                    <ActionButton size="sm" onClick={() => onViewHistoricalAuth('current')}>
                      View
                    </ActionButton>
                  </Flex>
                  {[...encounter.authentication.previousVersions].reverse().map((auth, i) => (
                    <Flex key={i} align="center" w="100%" gap={2} px={3} py={2}>
                      <Box flex={1} minW={0}>
                        <Text fontWeight={500}>
                          {auth.providerFirst} {auth.providerLast}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {/* §1.6: authentication timestamps render to the second. */}
                          {getDateTimeString(auth.authenticatedOn, 'short', false, true)}
                        </Text>
                      </Box>
                      <ActionButton size="sm" onClick={() => onViewHistoricalAuth(auth)}>
                        View
                      </ActionButton>
                    </Flex>
                  ))}
                </VStack>
              </PopoverBody>
              <PopoverArrow bg="gray.50" />
            </PopoverContent>
          </Portal>
        </Popover>
      ) : null}
    </Flex>
  )
}
